import { shippingComplete, shippingSetupMessage } from "@/lib/productShipping";
import { useLang } from "@/i18n";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, Package, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api, money, apiError } from "@/lib/api";
import { toast } from "sonner";
import StatCard from "@/components/admin/StatCard";
import EmptyState from "@/components/admin/EmptyState";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { ActiveBadge, StockBadge } from "@/components/admin/StatusBadge";
import { inputCls } from "@/components/admin/FormField";

const ShippingBadge = ({product}) => {
  const {lang} = useLang();
  return <div className="mt-1 text-xs"><p>{Number(product.shipping_weight) > 0 ? `${product.shipping_weight} ${product.weight_unit || ''}` : ''}</p><p>{shippingSetupMessage(product,lang)}</p></div>;
};

export default function Products() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [shippingFilter, setShippingFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, []);

  const catName = (slug) => cats.find((c) => c.slug === slug)?.name_en || slug;

  const kpis = useMemo(() => {
    const list = items || [];
    const active = list.filter((p) => p.active !== false).length;
    const lowStock = list.filter((p) => p.stock > 0 && p.stock < 10).length;
    const outOfStock = list.filter((p) => p.stock <= 0).length;
    return { total: list.length, active, lowStock, outOfStock };
  }, [items]);

  const filtered = useMemo(() => {
    const list = items || [];
    const q = search.trim().toLowerCase();
    return list.filter((p) => {
      if (q) {
        const hay = `${p.name_en} ${p.name_fr} ${p.sku || ""} ${catName(p.category_slug)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (shippingFilter === "ready" && !shippingComplete(p)) return false;
      if (shippingFilter === "missing" && shippingComplete(p)) return false;
      if (categoryFilter !== "all" && p.category_slug !== categoryFilter) return false;
      if (statusFilter === "active" && p.active === false) return false;
      if (statusFilter === "inactive" && p.active !== false) return false;
      if (stockFilter === "in" && !(p.stock > 0)) return false;
      if (stockFilter === "low" && !(p.stock > 0 && p.stock < 10)) return false;
      if (stockFilter === "out" && !(p.stock <= 0)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, categoryFilter, stockFilter, statusFilter, shippingFilter, cats]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleteId}`);
      toast.success("Product deleted");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setDeleteId(null);
    load();
  };

  const hasFilters = shippingFilter !== "all" || search || categoryFilter !== "all" || stockFilter !== "all" || statusFilter !== "all";

  return (
    <div data-testid="admin-products">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold text-navy">Products</h1>
        <Link to="/admin/products/new" className="btn-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="add-product-btn">
          <Plus size={16} /> Add Product
        </Link>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Products" value={items ? kpis.total : "…"} icon={Package} colorClass="text-navy" />
        <StatCard label="Active" value={items ? kpis.active : "…"} icon={CheckCircle2} colorClass="text-palm-500" />
        <StatCard label="Low Stock" value={items ? kpis.lowStock : "…"} icon={AlertTriangle} colorClass="text-amber-600" />
        <StatCard label="Out of Stock" value={items ? kpis.outOfStock : "…"} icon={XCircle} colorClass="text-red-500" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or SKU…"
            className={`${inputCls} pl-9`}
            data-testid="product-search"
          />
        </div>
        <select aria-label="Shipping readiness" data-testid="shipping-readiness-filter" value={shippingFilter} onChange={e => setShippingFilter(e.target.value)} className={`${inputCls} w-auto`}>
          <option value="all">All</option><option value="ready">Shipping Ready</option><option value="missing">Shipping Data Missing</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className={`${inputCls} w-auto`}>
          <option value="all">All Categories</option>
          {cats.map((c) => <option key={c.id} value={c.slug}>{c.name_en}</option>)}
        </select>
        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className={`${inputCls} w-auto`}>
          <option value="all">All Stock</option>
          <option value="in">In Stock</option>
          <option value="low">Low Stock</option>
          <option value="out">Out of Stock</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} w-auto`}>
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {items === null ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-white/60" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Package} title={hasFilters ? "No products match your filters" : "No products yet"} hint={hasFilters ? "Try adjusting your search or filters." : "Add your first product to get started."} />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-2xl border border-border bg-white card-shadow md:block">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-cream text-xs uppercase text-muted-foreground">
                <tr><th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover bg-cream" />
                        <div className="min-w-0">
                          <p className="truncate font-medium text-navy">{p.name_en}</p>
                          {p.sku && <p className="truncate text-xs text-muted-foreground">{p.sku}</p>}<ShippingBadge product={p} />
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-navy/70">{catName(p.category_slug)}</td>
                    <td className="p-3 text-navy">
                      {p.sale_price ? (
                        <span><span className="text-navy">{money(p.sale_price)}</span> <span className="text-xs text-muted-foreground line-through">{money(p.price)}</span></span>
                      ) : money(p.price)}
                    </td>
                    <td className="p-3"><StockBadge stock={p.stock} /></td>
                    <td className="p-3"><ActiveBadge active={p.active !== false} /></td>
                    <td className="p-3">
                      <div className="flex gap-3">
                        <Link to={`/admin/products/${p.id}`} className="text-ocean" data-testid={`edit-product-${p.slug}`} aria-label="Edit"><Pencil size={16} /></Link>
                        <button onClick={() => setDeleteId(p.id)} className="text-sunset" aria-label="Delete"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="grid gap-3 md:hidden">
            {filtered.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-white p-4 card-shadow">
                <div className="flex items-start gap-3">
                  <img src={p.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover bg-cream" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-head font-semibold text-navy">{p.name_en}</p>
                    <p className="text-xs text-muted-foreground">{catName(p.category_slug)}{p.sku ? ` · ${p.sku}` : ""}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <ActiveBadge active={p.active !== false} />
                    </div><ShippingBadge product={p} />
                  </div>
                  <div className="flex gap-3">
                    <Link to={`/admin/products/${p.id}`} className="text-ocean" aria-label="Edit"><Pencil size={16} /></Link>
                    <button onClick={() => setDeleteId(p.id)} className="text-sunset" aria-label="Delete"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-sm">
                  <span className="font-semibold text-navy">{money(p.sale_price || p.price)}</span>
                  <StockBadge stock={p.stock} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Delete this product?"
        description="This will permanently remove the product from the catalog. This cannot be undone."
        onConfirm={confirmDelete}
      />
    </div>
  );
}
